<script setup lang="ts">
import { computed } from 'vue'
import { VBadge } from '@/modules/shared/components'
import { useWorkspaceOnboardingStore } from '../../stores/workspace-onboarding-store'
import { useCandidateStore } from '@/modules/connecting/stores/candidate-store'
import { useTeamMemberStore } from '@/modules/connecting/stores/team-member-store'

const emit = defineEmits<{
  'navigate-to': [stepIndex: number]
}>()

const onboardingStore = useWorkspaceOnboardingStore()
const candidateStore = useCandidateStore()
const teamMemberStore = useTeamMemberStore()

const candidate = computed(() => {
  const id = onboardingStore.workspace?.connecting_candidate_id
  return id ? candidateStore.getCandidate(id.toString()) : undefined
})

const ws = computed(() => onboardingStore.workspace)

const formatCurrency = (value: number | string | null | undefined): string => {
  if (!value) return '\u2014'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num) || num === 0) return '\u2014'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num)
}

const rows = computed(() => {
  const c = candidate.value
  const w = ws.value

  const firmTypes =
    (c?.investment_types_interested || [])
      .map((t: string) => {
        const labels: Record<string, string> = { fund: 'Fund', syndicate: 'Syndicate', angel_group: 'Angel Group' }
        const mins: Record<string, string | null | undefined> = {
          fund: c?.fund_investment_min,
          syndicate: c?.spv_investment_min,
          angel_group: c?.deal_investment_min,
        }
        const min = formatCurrency(mins[t])
        return min !== '\u2014' ? `${labels[t] || t} (${min} min)` : labels[t] || t
      })
      .join(', ') || '\u2014'

  const stages = (c?.investment_stage || []).join(', ') || '\u2014'
  const industries = (c?.industries_interested || []).filter((i: string) => i !== 'Other...').join(', ') || '\u2014'
  const thesis = c?.investment_thesis
    ? c.investment_thesis.length > 80
      ? c.investment_thesis.slice(0, 80) + '...'
      : c.investment_thesis
    : '\u2014'
  const university = c?.university_affiliations?.length ? c.university_affiliations.join(', ') : 'Not required'
  const workspace = w?.name ? `${w.name} \u2014 ${w.subdomain}.venture360.co` : '\u2014'
  const website = w?.website || '\u2014'
  const phone = w?.phone || '\u2014'
  const timezone = w?.time_zone || '\u2014'
  const currency = w?.default_currency_code || '\u2014'
  const ria = w?.is_registered_investment_advisor
    ? `Yes \u2014 CRD ${w.central_registration_depository_number || '(not set)'}`
    : 'No'
  const setupMode = w?.setup_mode ? 'Enabled' : 'Disabled'

  const hasInvestmentMin =
    !!(c?.fund_investment_min && parseFloat(c.fund_investment_min) > 0) ||
    !!(c?.deal_investment_min && parseFloat(c.deal_investment_min) > 0) ||
    !!(c?.spv_investment_min && parseFloat(c.spv_investment_min) > 0)

  const firmTypeIncomplete = !c?.investment_types_interested?.length || !hasInvestmentMin
  const stagesIncomplete = !c?.investment_stage?.length
  const thesisIncomplete = !c?.investment_thesis

  return [
    { key: 'Firm type', value: firmTypes, stepIndex: 0, incomplete: firmTypeIncomplete },
    { key: 'Stages', value: stages, stepIndex: 1, incomplete: stagesIncomplete },
    { key: 'Industries', value: industries, stepIndex: 2 },
    { key: 'Thesis', value: thesis, stepIndex: 3, incomplete: thesisIncomplete },
    { key: 'University', value: university, stepIndex: 4 },
    {
      key: 'Team',
      value: teamMemberStore.members.length ? teamMemberStore.members.map((m) => m.name).join(', ') : '\u2014',
      stepIndex: 5,
    },
    { key: 'Workspace', value: workspace, stepIndex: 6 },
    { key: 'Website', value: website, stepIndex: 6 },
    { key: 'Phone', value: phone, stepIndex: 6 },
    { key: 'Time zone', value: timezone, stepIndex: 7 },
    { key: 'Currency', value: currency, stepIndex: 7 },
    { key: 'RIA', value: ria, stepIndex: 8 },
    { key: 'Setup mode', value: setupMode, stepIndex: 9 },
  ]
})

const submit = async (): Promise<boolean> => true

defineExpose({ submit })
</script>

<template>
  <div>
    <button
      v-for="row in rows"
      :key="row.key"
      type="button"
      class="mb-[6px] flex w-full cursor-pointer items-center gap-4 rounded-md border-none bg-white px-4 py-[14px] text-left font-[inherit] shadow-sm transition-shadow duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-md active:translate-y-px active:shadow-sm"
      :class="
        row.incomplete
          ? 'ring-1 ring-amber-400/60 hover:ring-amber-400/80'
          : 'ring-1 ring-gray-700/20 hover:ring-gray-700/30'
      "
      @click="emit('navigate-to', row.stepIndex)"
    >
      <span class="shrink-0 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.5px] text-[#9ca3af]">{{
        row.key
      }}</span>
      <VBadge v-if="row.incomplete" color="amber" size="xxs">Required</VBadge>
      <span
        class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-right text-[13px] font-medium text-[#374151]"
        >{{ row.value }}</span
      >
      <svg
        class="shrink-0 text-[#d1d5db]"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  </div>
</template>
